// ─── "From your coach": daily habits ───
// Home card for the yes/no habits a coach assigned: a big check for today, the Mon–Sun
// dots of this week (today and the previous six days can be ticked) and the weekly
// count against the target. Ticks are optimistic and roll back with a toast on error.
// API: my_habits / log_habit (ironlog-api/api/org/index.mjs).
import React, { useState, useRef, useCallback } from 'react';
import { V, Haptic } from '../utils/theme';
import { Icons } from './Icons';
import { Card, SuccessToastCtrl } from './ui';
import { onAccent } from './kit';
import { today } from '../utils/helpers';
import { orgApi, orgErr, errorMessage } from '../utils/org';
import { HABITS_CACHE_KEY, mondayOf, weekDots, weekDoneCount, toggleHabitDate, loggableDate } from '../utils/coachForms';
import { useOrgFeed } from '../utils/orgFeed';

const SR_ONLY = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

const patchHabit = (data, id, fn) => (data && Array.isArray(data.habits) ? { ...data, habits: data.habits.map(h => (h.id === id ? fn(h) : h)) } : data);

function habitError(er) {
  if (er.code === 'habit_archived' || er.code === 'not_found' || er.status === 404) return 'Your coach removed this habit.';
  if (er.code === 'invalid_date') return 'You can tick today or the previous six days.';
  if (er.code === 'network') return "Couldn't save that tick — check your connection.";
  return errorMessage(er);
}

function HabitRow({ habit, td, weekStart, onToggle, first }) {
  const dots = weekDots(habit.done_dates, weekStart, td);
  const doneToday = dots.some(x => x.today && x.done);
  const count = weekDoneCount(habit.done_dates, weekStart, td);
  const target = habit.target_per_week || 7;
  const hit = count >= target;
  return (
    <li data-testid="habit-row" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', paddingTop: 10, paddingBottom: 10, borderTop: first ? 'none' : `1px solid ${V.divider}` }}>
      <button type="button" aria-pressed={doneToday} aria-label={`${habit.title}: done today`} data-testid="habit-today"
        onClick={() => onToggle(habit, td, !doneToday)}
        style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: doneToday ? `linear-gradient(135deg,${V.accent},${V.accent2})` : V.inputBg,
          border: `2px solid ${doneToday ? V.accent : V.cardBorder}`, boxShadow: doneToday ? V.glow : 'none',
          transition: 'background .15s, border-color .15s', WebkitTapHighlightColor: 'transparent', padding: 0 }}>
        {Icons.check({ size: 24, color: doneToday ? onAccent() : V.text3, strokeWidth: 3 })}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: V.text, overflowWrap: 'anywhere' }}>{habit.title}</div>
          <div data-testid="habit-count" style={{ flexShrink: 0, fontSize: 12, fontFamily: V.mono, fontWeight: 700, color: hit ? V.accent : V.text2 }}>
            {count} / {target}<span style={{ fontFamily: V.font, fontWeight: 400, color: V.text3 }}> this week</span>
          </div>
        </div>
        {habit.description && <div style={{ fontSize: 11, color: V.text3, marginTop: 2, lineHeight: 1.4 }}>{habit.description}</div>}
        <div role="group" aria-label={`${habit.title}, this week`} style={{ display: 'flex', gap: 4, marginTop: 8 }}>
          {dots.map(x => (
            <button key={x.date} type="button" disabled={!x.loggable} aria-pressed={x.done}
              aria-label={`${x.name}${x.today ? ' (today)' : ''}${x.future ? ', not yet' : ''}`}
              onClick={() => x.loggable && onToggle(habit, x.date, !x.done)}
              style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: 'none', border: 'none',
                paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0, cursor: x.loggable ? 'pointer' : 'default', WebkitTapHighlightColor: 'transparent' }}>
              <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
                background: x.done ? V.accent : 'transparent',
                border: x.done ? `2px solid ${V.accent}` : x.future ? `1.5px dashed ${V.cardBorder}` : `2px solid ${x.today ? V.accent : V.text3}`,
                opacity: x.future ? 0.5 : 1 }}>
                {x.done && Icons.check({ size: 12, color: onAccent(), strokeWidth: 3.2 })}
              </span>
              <span aria-hidden="true" style={{ fontSize: 10, fontWeight: x.today ? 800 : 600, color: x.today ? V.accent : V.text3 }}>{x.letter}</span>
            </button>
          ))}
        </div>
      </div>
    </li>
  );
}

export function CoachHabitsCard() {
  const inflight = useRef({});   // habit id → writes in flight
  const latest = useRef({});     // `${habit id}|${date}` → newest write sequence
  const seq = useRef(0);
  const canApply = useCallback(() => Object.values(inflight.current).every(n => !n), []);
  const { enabled, data, setData, load } = useOrgFeed('my_habits', { kind: 'habits', cacheKey: HABITS_CACHE_KEY, canApply });
  const [live, setLive] = useState('');
  const habits = Array.isArray(data && data.habits) ? data.habits : [];
  if (!enabled || !habits.length) return null;
  const td = today();
  const weekStart = mondayOf(td);

  const toggle = async (habit, date, done) => {
    const now = today();
    if (!loggableDate(date, now)) return;
    Haptic.light();
    const k = `${habit.id}|${date}`;
    const my = ++seq.current;
    latest.current[k] = my;
    inflight.current[habit.id] = (inflight.current[habit.id] || 0) + 1;
    setData(d => patchHabit(d, habit.id, h => toggleHabitDate(h, date, done, now)));
    setLive(`${habit.title}: ${done ? 'done' : 'not done'}${date === now ? ' today' : ''}`);
    try {
      const r = await orgApi('log_habit', { habit_id: habit.id, date, done, today: now });
      inflight.current[habit.id] -= 1;
      // Only the last write in flight carries the whole truth for this habit
      if (!inflight.current[habit.id] && r && r.habit && r.habit.id === habit.id) setData(d => patchHabit(d, habit.id, h => ({ ...h, ...r.habit })));
    } catch (e) {
      inflight.current[habit.id] -= 1;
      const er = orgErr(e);
      if (latest.current[k] === my) setData(d => patchHabit(d, habit.id, h => toggleHabitDate(h, date, !done, now)));
      const msg = habitError(er);
      SuccessToastCtrl.error(msg);
      setLive(msg);
      if (er.code === 'habit_archived' || er.status === 404) load();
    }
  };

  return (
    <div data-testid="coach-habits-card">
      <Card style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: V.accent, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 2 }}>From your coach</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: V.text }}>Daily habits</div>
          </div>
          {Icons.target({ size: 18, color: V.accent })}
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {habits.map((h, i) => <HabitRow key={h.id} habit={h} td={td} weekStart={weekStart} onToggle={toggle} first={i === 0} />)}
        </ul>
        <div aria-live="polite" style={SR_ONLY}>{live}</div>
      </Card>
    </div>
  );
}
